import { useQuery } from '@tanstack/react-query';
import { searchRides } from '../lib/api';
import { BackButton } from '../lib/BackButton';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { translateError, useT } from '../lib/i18n';
import { humanDate } from '../lib/datetime';
import { useState } from 'react';
import { RideCard } from '../components/RideCard';
import { RouteAlertSheet } from '../components/RouteAlertSheet';
import { RetryCard } from '../components/RetryCard';
import { PullIndicator } from '../components/PullIndicator';
import { usePullToRefresh } from '../lib/usePullToRefresh';

export function SearchResultsPage(): JSX.Element {
  const { navigate, search } = useRouter();
  const { me } = useSession();
  const { t, locale } = useT();
  const [alertOpen, setAlertOpen] = useState(false);
  const originCityId = search.get('o') ?? '';
  const destinationCityId = search.get('d') ?? '';
  const date = search.get('date') ?? '';
  const womenOnly = search.get('women') === '1';
  const maxPrice = search.get('maxPrice') ?? undefined;
  const minRating = search.get('minRating') ?? undefined;

  const q = useQuery({
    queryKey: ['rides', originCityId, destinationCityId, date, womenOnly, maxPrice, minRating],
    queryFn: () =>
      searchRides({
        originCityId,
        destinationCityId,
        date,
        womenOnly: womenOnly || undefined,
        maxPrice,
        minRating,
      }),
    enabled: Boolean(originCityId && destinationCityId && date),
  });
  const ptr = usePullToRefresh({ onRefresh: () => q.refetch() });

  return (
    <div
      {...ptr.bind}
      style={{ transform: `translateY(${ptr.offset}px)`, transition: ptr.offset === 0 ? 'transform 200ms ease-out' : undefined }}
      className="relative mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6"
    >
      <PullIndicator offset={ptr.offset} refreshing={ptr.refreshing} />
      <BackButton />
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-tg-text">{t('search.title')}</h1>
        <p className="text-sm text-tg-hint">{date ? humanDate(date, locale, t) : ''}</p>
      </header>

      {q.isLoading && (
        <div className="space-y-3">
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      )}

      {q.isError && (
        <RetryCard
          message={translateError(t, q.error as Error)}
          onRetry={() => q.refetch()}
          isRetrying={q.isFetching}
        />
      )}

      {q.data && q.data.items.length === 0 && (
        <div className="rounded-card border border-[var(--border-light)] bg-tg-bg-secondary p-6 text-center">
          <p className="font-semibold text-tg-text">{t('search.empty.title')}</p>
          <p className="mt-1 text-sm text-tg-hint">{t('search.empty.body')}</p>
          <div className="mt-4 flex flex-col items-center gap-2">
            {originCityId && destinationCityId && (
              <button
                type="button"
                onClick={() => setAlertOpen(true)}
                className="inline-flex min-h-[44px] items-center rounded-pill bg-brand-green px-4 text-sm font-semibold text-white"
              >
                {t('search.empty.setAlert')}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex min-h-[44px] items-center rounded-pill bg-tg-bg px-4 text-sm font-semibold text-tg-text"
            >
              {t('search.empty.changeSearch')}
            </button>
            {/* "Publish a ride" is only useful for drivers — don't send passengers there. */}
            {me?.isDriver && (
              <button
                type="button"
                onClick={() => navigate('/publish')}
                className="inline-flex min-h-[44px] items-center rounded-pill bg-brand-green px-4 text-sm font-semibold text-white"
              >
                {t('search.empty.cta')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {q.data?.items.map((r, i) => (
          <div
            key={r.id}
            className="animate-card-in"
            // Capped stagger so a long list doesn't feel slow.
            style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
          >
            <RideCard
              ride={r}
              onOpen={() =>
                navigate(
                  r.matchedSegment
                    ? `/ride/${r.id}?from=${r.matchedSegment.fromCityId}&to=${r.matchedSegment.toCityId}`
                    : `/ride/${r.id}`,
                )
              }
            />
          </div>
        ))}
      </div>

      {alertOpen && (
        <RouteAlertSheet
          originCityId={originCityId}
          destinationCityId={destinationCityId}
          onClose={() => setAlertOpen(false)}
        />
      )}
    </div>
  );
}

function Skeleton(): JSX.Element {
  return <div className="h-32 animate-pulse rounded-card bg-tg-bg-secondary" />;
}
